/**
 * Préférences et consentements (RM-06).
 *
 * Deux natures de réglages cohabitent ici, et la différence n'est pas cosmétique :
 * le partage de progression et les emails d'actualité sont des **consentements**,
 * datés et consignés au journal (docs/20 §7) ; le thème est une préférence
 * d'affichage. L'écran le dit — `consentNotice` — parce qu'un consentement dont
 * on ignore qu'il est tracé n'est pas vraiment éclairé.
 *
 * Chaque case s'enregistre à la volée : une case cochée qu'il faut ensuite
 * penser à valider est le meilleur moyen de croire avoir retiré un
 * consentement qui court toujours.
 */
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { useUpdateSettings, type Settings } from "@/features/account/api/useAccount";
import { useApiErrorMessage } from "@/features/auth/lib/useApiErrorMessage";
import { Alert } from "@/shared/ui/Alert";
import { Card } from "@/shared/ui/Card";
import { THEMES, toTheme, type Theme } from "@/shared/theme/theme";

interface PreferencesFormProps {
  settings: Settings;
  canEdit: boolean;
}

/**
 * État affiché. `theme` y est un `Theme` et non un `string` : la réponse d'API
 * le déclare libre (docs/08 §2 — une énumération en réponse ne peut plus
 * s'étendre), et c'est ici qu'on le ramène à une valeur du catalogue.
 */
interface Draft {
  share_progress_with_teachers: boolean;
  marketing_emails: boolean;
  theme: Theme;
}

function toDraft(settings: Settings): Draft {
  return {
    share_progress_with_teachers: settings.share_progress_with_teachers,
    marketing_emails: settings.marketing_emails,
    theme: toTheme(settings.theme),
  };
}

export function PreferencesForm({ settings, canEdit }: PreferencesFormProps) {
  const { t } = useTranslation("account");
  const update = useUpdateSettings();
  const errorMessage = useApiErrorMessage();
  const fieldId = useId();

  const [draft, setDraft] = useState(() => toDraft(settings));

  // La réponse du serveur fait autorité : un enregistrement refusé doit rendre
  // la case à son état réel, et non laisser à l'écran un consentement que la
  // base n'a pas.
  useEffect(() => {
    setDraft(toDraft(settings));
  }, [settings]);

  const save = (change: Partial<Draft>) => {
    // Affichage optimiste : une case qui n'obéit qu'après l'aller-retour donne
    // l'impression d'un clic perdu.
    setDraft({ ...draft, ...change });
    update.mutate(change, {
      // …mais un refus doit défaire l'optimisme. Sans ce retour en arrière, la
      // case resterait cochée à l'écran alors que la base n'a rien enregistré,
      // et l'utilisateur croirait avoir donné — ou retiré — un consentement
      // qui ne l'a pas été. `settings` ne change pas sur un échec : rien
      // d'autre ne remettrait la case en place.
      onError: () => {
        setDraft(toDraft(settings));
      },
    });
  };

  return (
    <Card title={t("preferences.heading")}>
      <div className="space-y-4">
        <Consent
          id={`${fieldId}-share`}
          label={t("preferences.shareProgress")}
          hint={t("preferences.shareProgressHint")}
          checked={draft.share_progress_with_teachers}
          disabled={!canEdit || update.isPending}
          onChange={(checked) => {
            save({ share_progress_with_teachers: checked });
          }}
        />
        <Consent
          id={`${fieldId}-marketing`}
          label={t("preferences.marketingEmails")}
          hint={t("preferences.marketingEmailsHint")}
          checked={draft.marketing_emails}
          disabled={!canEdit || update.isPending}
          onChange={(checked) => {
            save({ marketing_emails: checked });
          }}
        />

        <p className="text-xs text-slate-500 dark:text-slate-400">
          {t("preferences.consentNotice")}
        </p>

        <div className="space-y-1.5">
          <label htmlFor={`${fieldId}-theme`} className="block text-sm font-medium">
            {t("preferences.theme")}
          </label>
          <select
            id={`${fieldId}-theme`}
            value={draft.theme}
            disabled={!canEdit || update.isPending}
            onChange={(event) => {
              save({ theme: toTheme(event.target.value) });
            }}
            className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm disabled:opacity-50 sm:w-64 dark:border-slate-700 dark:bg-slate-900"
          >
            {THEMES.map((theme) => (
              <option key={theme} value={theme}>
                {t(`preferences.themes.${theme}`)}
              </option>
            ))}
          </select>
        </div>

        {update.isError && <Alert tone="error">{errorMessage(update.error)}</Alert>}
        {update.isSuccess && <Alert tone="success">{t("saved")}</Alert>}
      </div>
    </Card>
  );
}

interface ConsentProps {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}

function Consent({ id, label, hint, checked, disabled, onChange }: ConsentProps) {
  return (
    <div className="flex gap-3">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-describedby={`${id}-hint`}
        onChange={(event) => {
          onChange(event.target.checked);
        }}
        className="text-brand-600 focus:ring-brand-500 mt-1 size-4 rounded border-slate-300 disabled:opacity-50 dark:border-slate-700"
      />
      <div className="space-y-0.5">
        <label htmlFor={id} className="block text-sm font-medium">
          {label}
        </label>
        <p id={`${id}-hint`} className="text-xs text-slate-500 dark:text-slate-400">
          {hint}
        </p>
      </div>
    </div>
  );
}
